import React from 'react';
import { autoReportCrash } from '../../services/incidentService';

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in the component tree and:
 *   1. Displays a clean fallback UI (no internal URLs or stack traces)
 *   2. Auto-sends an INC email to anything@tarr.ink on crash
 *   3. Offers a "Report Issue" button for the user to add context
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      reportSent: false,
      showReportForm: false,
      reportDescription: '',
      reportSubmitting: false,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Auto-send incident email (fire-and-forget, rate-limited)
    autoReportCrash(error, errorInfo?.componentStack || '').then(() => {
      this.setState({ reportSent: true });
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showReportForm: false,
      reportDescription: '',
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleOpenReportForm = () => {
    this.setState({ showReportForm: true });
  };

  handleSubmitReport = async () => {
    const { error, errorInfo, reportDescription } = this.state;
    this.setState({ reportSubmitting: true });

    try {
      const { reportIncident } = await import('../../services/incidentService');
      await reportIncident({
        type: 'bug',
        severity: 'high',
        description: reportDescription,
        errorMessage: error?.message || '',
        stackTrace: error?.stack || '',
        componentStack: errorInfo?.componentStack || '',
      });
      this.setState({ reportSubmitting: false, showReportForm: false, reportDescription: '' });
    } catch {
      this.setState({ reportSubmitting: false });
    }
  };

  render() {
    const {
      hasError, errorCount, showReportForm, reportDescription,
      reportSubmitting, reportSent,
    } = this.state;
    const { children, fallback, onReset } = this.props;

    if (!hasError) return children;

    // Custom fallback provided by parent
    if (fallback) {
      return typeof fallback === 'function'
        ? fallback(this.state.error, this.handleReset)
        : fallback;
    }

    const isDarkMode = document.body.classList.contains('dark-mode');
    const bg = isDarkMode ? '#1a1a1a' : '#ffffff';
    const fg = isDarkMode ? '#e0e0e0' : '#000000';
    const muted = isDarkMode ? '#666' : '#999';
    const btnBg = isDarkMode ? '#fff' : '#000';
    const btnFg = isDarkMode ? '#000' : '#fff';
    const borderColor = isDarkMode ? '#444' : '#ccc';
    const inputBg = isDarkMode ? '#2d2d2d' : '#f5f5f5';

    return (
      <div
        style={{
          padding: '40px 20px',
          textAlign: 'center',
          fontFamily: '"IBM Plex Mono", monospace',
          backgroundColor: bg,
          color: fg,
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ maxWidth: '500px', width: '100%' }}>
          {/* Title */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
            Something went wrong
          </h2>

          {/* Message */}
          <p style={{ fontSize: '13px', color: muted, marginBottom: '24px', lineHeight: '1.6' }}>
            {errorCount > 2
              ? 'This section is experiencing repeated errors. Please refresh the page.'
              : 'An unexpected error occurred. Our team has been notified.'}
          </p>

          {/* Report sent indicator */}
          {reportSent && !showReportForm && (
            <p style={{ fontSize: '12px', color: muted, marginBottom: '16px' }}>
              Crash report sent automatically.
            </p>
          )}

          {/* Report Issue Form */}
          {showReportForm && (
            <div style={{ marginBottom: '24px', textAlign: 'left' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                What were you doing when this happened?
              </label>
              <textarea
                value={reportDescription}
                onChange={(e) => this.setState({ reportDescription: e.target.value })}
                placeholder="Describe what you were doing..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  fontSize: '13px',
                  fontFamily: '"IBM Plex Mono", monospace',
                  backgroundColor: inputBg,
                  color: fg,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '0px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={this.handleSubmitReport}
                  disabled={reportSubmitting}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontWeight: '600',
                    backgroundColor: btnBg,
                    color: btnFg,
                    border: 'none',
                    borderRadius: '0px',
                    cursor: reportSubmitting ? 'wait' : 'pointer',
                    opacity: reportSubmitting ? 0.6 : 1,
                  }}
                >
                  {reportSubmitting ? 'Sending...' : 'Send Report'}
                </button>
                <button
                  onClick={() => this.setState({ showReportForm: false })}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontFamily: '"IBM Plex Mono", monospace',
                    backgroundColor: 'transparent',
                    color: muted,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '0px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {errorCount <= 2 && (
              <button
                onClick={() => { this.handleReset(); if (onReset) onReset(); }}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontWeight: '600',
                  backgroundColor: btnBg,
                  color: btnFg,
                  border: 'none',
                  borderRadius: '0px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            )}

            <button
              onClick={this.handleReload}
              style={{
                padding: '10px 20px',
                fontSize: '13px',
                fontFamily: '"IBM Plex Mono", monospace',
                fontWeight: '600',
                backgroundColor: 'transparent',
                color: muted,
                border: `1px solid ${borderColor}`,
                borderRadius: '0px',
                cursor: 'pointer',
              }}
            >
              Refresh Page
            </button>

            {!showReportForm && (
              <button
                onClick={this.handleOpenReportForm}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontWeight: '600',
                  backgroundColor: 'transparent',
                  color: muted,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '0px',
                  cursor: 'pointer',
                }}
              >
                Report Issue
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
